import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { environment } from '../../environments/environment';
import { ResponseTranslation } from '@card-software/nexus-models';

/**
 * Minimal ItemMaster shape — only the fields Cost Analysis needs. Mirrors
 * Item.API ItemMaster. Extend as other features need more.
 *
 * The discriminator fields below identify which level of the Yarn → Greige →
 * Fabric hierarchy a given Item Master sits at: exactly one of `baseYarnCode`,
 * `baseGreigeCode`, `baseFabricCode` is set. `itemCategoryCode` is a redundant
 * but authoritative discriminator (`X00010000000` Fabric, `X00020000000` Yarn,
 * `X00030000000` Greige). Cost Analysis costs only Fabric Item Masters; Greige
 * and Yarn IMs are excluded from the costable hierarchy.
 */
export interface NumberedDocumentKey {
  reference: string | null;
  number: string | null;
}
export interface ItemMasterDto {
  id?: string;
  itemCode?: string;
  baseFabricCode?: string;
  baseGreigeCode?: string;
  baseYarnCode?: string;
  itemCategoryCode?: string;
  /** Item classification MasterData code (#4729) — drives the Service-only withholding-certificate filter. */
  itemClassificationCode?: string;
  itemDisplayName: ResponseTranslation;
  itemLongDisplayName: ResponseTranslation;
  /** Item-master unit of measure (UnitOfMeasure enum code from Item.API). */
  unitOfMeasure?: number;
  glAccounts?: NumberedDocumentKey[];
}

/** Search command for the item type-ahead — mirrors Item.API ItemSearchCommand. */
export interface ItemSearchCommand {
  itemCategoryCode?: string;
  name?: string;
  itemCode?: string;
  inventoryTypeReference?: string;
  status?: number[];
}

@Injectable({ providedIn: 'root' })
export class ItemMasterProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.itemApi + '/api/v1/item';

  public getByItemCodes(itemCodes: string[]): Observable<ApiResponse<ItemMasterDto[]>> {
    return this.httpClient.post<ApiResponse<ItemMasterDto[]>>(`${this.baseUrl}/item-codes/batch`, { itemCodes });
  }

  /** Type-ahead item search used by the AP invoice line grid. Mirrors Order.UI. */
  public postItemSearch(
    dataSearch: ItemSearchCommand,
    matchAny = false,
    consumableOnly = false,
    includeInactive = false,
    excludeProductDevelopments = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ItemMasterDto>>> {
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ItemMasterDto>>>(
      `${this.baseUrl}/search?matchAny=${matchAny}&consumableOnly=${consumableOnly}&includeInactive=${includeInactive}&excludeProductDevelopments=${excludeProductDevelopments}`,
      dataSearch,
    );
  }
}
